// apps/web/src/components/privacy/PrivacyManager.tsx
// Privacy management component

import type React from 'react';
import { useState, useEffect } from 'react';
import type { PrivacyConsent } from '../../types/privacy';
import { apiClient } from '../../lib/apiClient';

export function PrivacyManager() {
  const [consents, setConsents] = useState<PrivacyConsent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showConsentForm, setShowConsentForm] = useState(false);

  useEffect(() => {
    const fetchConsents = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/api/v1/privacy/consents');
        setConsents(response.data.consents);
      } catch (err) {
        setError('Failed to load consents');
        console.error('Error loading consents:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchConsents();
  }, []);

  const handleCreateConsent = async (consentData: any) => {
    try {
      const response = await apiClient.post('/api/v1/privacy/consents', consentData);
      setConsents([response.data, ...consents]);
      setShowConsentForm(false);
    } catch (err) {
      setError('Failed to create consent');
      console.error('Error creating consent:', err);
    }
  };

  if (loading) {
    return <div className="loading">Loading privacy consents...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="privacy-manager">
      <div className="header">
        <h2>Privacy Management</h2>
        <button 
          onClick={() => setShowConsentForm(!showConsentForm)}
          className="btn btn-primary"
        >
          {showConsentForm ? 'Cancel' : 'Create Consent'}
        </button>
      </div>

      {showConsentForm && (
        <ConsentForm 
          onSubmit={handleCreateConsent} 
          onCancel={() => setShowConsentForm(false)} 
        />
      )}

      <div className="consents-grid">
        {consents.map(consent => (
          <ConsentCard key={consent.id} consent={consent} />
        ))}
      </div>
    </div>
  );
}

// Helper component
function ConsentCard({ consent }: { consent: PrivacyConsent }) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'status-active';
      case 'INACTIVE': return 'status-inactive';
      case 'EXPIRED': return 'status-expired';
      case 'WITHDRAWN': return 'status-withdrawn';
      default: return 'status-default';
    }
  };

  return (
    <div className="consent-card">
      <div className="consent-header">
        <h3>{consent.name}</h3>
        <span className={`status-badge ${getStatusColor(consent.status)}`}>
          {consent.status}
        </span>
      </div>
      <p>{consent.description || 'No description'}</p>
      <p><strong>Type:</strong> {consent.consentType}</p>
      <p><strong>Required:</strong> {consent.required ? 'Yes' : 'No'}</p>
      <p><strong>Created:</strong> {new Date(consent.createdAt).toLocaleDateString()}</p>
      <p><strong>Expires:</strong> {consent.expiresAt ? new Date(consent.expiresAt).toLocaleDateString() : 'Never'}</p>
      <div className="actions">
        <button className="btn btn-secondary">View</button>
        <button className="btn btn-warning">Edit</button>
        {consent.status === 'ACTIVE' && (
          <button className="btn btn-danger">Withdraw</button>
        )}
      </div>
    </div>
  );
}

// Helper component
function ConsentForm({ onSubmit, onCancel }: { onSubmit: any; onCancel: any }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    consentType: 'MARKETING',
    required: false,
    expiresAt: '',
    terms: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="consent-form">
      <h3>Create Privacy Consent</h3>
      <input
        type="text"
        placeholder="Consent Name"
        value={formData.name}
        onChange={(e) => setFormData({...formData, name: e.target.value})}
        required
      />
      <textarea
        placeholder="Description"
        value={formData.description}
        onChange={(e) => setFormData({...formData, description: e.target.value})}
      />
      <select
        value={formData.consentType}
        onChange={(e) => setFormData({...formData, consentType: e.target.value})}
      >
        <option value="MARKETING">Marketing</option>
        <option value="ANALYTICS">Analytics</option>
        <option value="ADVERTISING">Advertising</option>
        <option value="PERSONALIZATION">Personalization</option>
        <option value="SECURITY">Security</option>
        <option value="OTHER">Other</option>
      </select>
      <label>
        <input
          type="checkbox"
          checked={formData.required}
          onChange={(e) => setFormData({...formData, required: e.target.checked})}
        />
        Required Consent
      </label>
      <input
        type="date"
        value={formData.expiresAt}
        onChange={(e) => setFormData({...formData, expiresAt: e.target.value})}
      />
      <textarea
        placeholder="Terms and Conditions"
        value={formData.terms}
        onChange={(e) => setFormData({...formData, terms: e.target.value})}
      />
      <div className="form-actions">
        <button type="submit" className="btn btn-primary">Create Consent</button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
      </div>
    </form>
  );
}